import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { APIS } from '@/shared/config/urls';
import { fetchTranslation } from '@/shared/utils/i18n';
import { request } from '@/shared/utils/request';
import { useAuthStore } from '@/store/authStore';
import { ButtonStore } from '@/store/buttonStore';
import { useLoadingStore } from '@/store/loadingStore';

interface SwitchButtonProps {
  buttonStyle?: string;
  // onToggle: () => void;
  groupKey: string;
  id: string;
  typeButton?: 'text' | 'noText';
}

const SwitchButton = ({ typeButton = 'noText', id, groupKey, buttonStyle }: SwitchButtonProps) => {
  const { activeIdsMap, toggle } = ButtonStore();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  const activeIds = activeIdsMap[groupKey] || [];
  const isActive = activeIds.includes(id);
  const isLangEng = i18n.language === 'eng';

  return (
    <>
      {typeButton === 'noText' ? (
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          onClick={() => toggle(groupKey, id)}
          className={cn(
            'relative inline-flex h-[24px] w-[44px] shrink-0 cursor-pointer rounded-full border-2 ',
            'transition-colors duration-200 ease-in-out',
            isActive ? 'bg-[#ADD5FF] ' : 'bg-[#EAEAEA]/40 ',
            buttonStyle
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-[20px] w-[20px] transform rounded-full  shadow ring-0',
              'transition duration-200 ease-in-out',
              isActive ? 'translate-x-[20px] bg-text-blue-100' : 'translate-x-0 bg-[#D9D9D9]'
            )}
          />
        </button>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={isLangEng}
          onClick={async () => {
            const targetLang = isLangEng ? 'ind' : 'eng';

            // Loader global selama proses ganti bahasa (simpan preferensi +
            // fetch kamus) - tanpa ini UI tampak beku beberapa detik.
            const { setTranslationLoading } = useLoadingStore.getState();
            setTranslationLoading(true);
            try {
              // The backend serves /common/authenticated-user/languages from the
              // signed-in user's stored language_preference, not from a request
              // param, so the preference must be persisted before re-fetching or
              // the fetched text won't match the language just switched to.
              await request.put(APIS.updateLanguagePreference, {
                language_preference: targetLang,
              });
              await fetchTranslation(targetLang);

              await i18n.changeLanguage(targetLang);
              useAuthStore.getState().setLang(targetLang);
              // Sidebar/header menu labels come from /program-navbar already
              // translated by the backend from the stored preference, so the
              // list must be refetched or it keeps showing the old language.
              await queryClient.invalidateQueries({ queryKey: ['navbarList'] });
            } finally {
              setTranslationLoading(false);
            }
          }}
          className={cn(
            'relative h-[36px] w-[65px] rounded-full border border-[#C2D2E2] bg-[#EEF3FB] p-[2px]',
            buttonStyle
          )}
        >
          <div
            className={cn(
              'absolute top-[2px] left-[2px] h-[30px] w-[30px] rounded-full bg-[#1976F3]',
              'transition-transform duration-300 ease-in-out',
              isLangEng && 'translate-x-[29px]'
            )}
          />

          <div className="relative z-10 flex h-full">
            <span
              className={cn(
                'flex flex-1 items-center justify-center text-[12px] transition-colors duration-300',
                !isLangEng ? 'text-white' : 'text-[#6B7280] cursor-pointer'
              )}
            >
              ID
            </span>

            <span
              className={cn(
                'flex flex-1 items-center justify-center text-[12px] transition-colors duration-300',
                isLangEng ? 'text-white' : 'text-[#6B7280] cursor-pointer'
              )}
            >
              EN
            </span>
          </div>
        </button>
      )}
    </>
  );
};

export default SwitchButton;
